import { useAuth } from '@/context/auth-context'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { DevLogin } from './dev-login'
import { GoogleSignInLegacy } from './google-signin-legacy'

// Declare the google global variable
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void
          renderButton: (element: HTMLElement, config: any) => void
          prompt: () => void
        }
      }
    }
  }
}

export function Login() {
  const { login, error, loading, user } = useAuth()
  const [initError, setInitError] = useState<string | null>(null)
  const [googleLoaded, setGoogleLoaded] = useState(false)
  
  // Debug auth state
  useEffect(() => {
    console.log('Login component - Auth state:', { user, loading, error })
  }, [user, loading, error])

  useEffect(() => {
    // Check if Google script is already loaded
    if (window.google?.accounts?.id) {
      setGoogleLoaded(true)
      initializeGoogleSignIn()
      return
    }
    
    // Load the Google Sign-In API script
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      setGoogleLoaded(true)
      initializeGoogleSignIn()
    }
    script.onerror = () => {
      setInitError('Failed to load Google authentication. Please try again later.')
    }
    document.body.appendChild(script)

    return () => {
      // Only remove if it exists
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  // Separate effect to handle initialization after Google script is loaded
  // We're removing this second initialization to prevent duplicate requests
  // The script.onload handler already calls initializeGoogleSignIn()

  const initializeGoogleSignIn = () => {
    if (window.google && window.google.accounts) {
      try {
        // Check if button already exists to prevent re-initialization
        const buttonDiv = document.getElementById('google-signin')
        if (buttonDiv && buttonDiv.innerHTML) {
          console.log('Google Sign-In already initialized')
          return
        }
        
        console.log('Initializing Google Sign-In with client ID:', import.meta.env.VITE_GOOGLE_CLIENT_ID)
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin',
          use_fedcm_for_prompt: false, // Disable FedCM
          itp_support: true,
          error_callback: () => {
            console.error('Google Sign-In error - likely origin not allowed');
            setInitError('Google Sign-In is not configured for this domain. Please use development login below.');
          }
        })

        // Render the button
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            type: 'standard',
            size: 'large',
            theme: 'outline',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 250,
          })
          
          // Don't use One Tap if FedCM is causing issues
          // window.google.accounts.id.prompt()
        }
      } catch (error) {
        console.error('Google Sign-In initialization error:', error)
        setInitError(`Failed to initialize Google authentication: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    } else {
      console.error('Google API not available')
    }
  }

  const handleCredentialResponse = (response: any) => {
    console.log('Google credential response received:', response)
    if (response.credential) {
      login(response.credential)
    }
  }

  // Clear the force_logout flag when the login component mounts
  useEffect(() => {
    localStorage.removeItem('force_logout')
  }, [])

  // Get location state and URL params to handle redirects after login
  const location = useLocation()
  const [searchParams] = useSearchParams()
  
  // Check for return URL in query params first, then fall back to location state
  const returnUrl = searchParams.get('return')
  const from = returnUrl || location.state?.from?.pathname || '/dashboard'
  
  // If user is already authenticated, redirect to the page they were trying to access
  if (user && !loading) {
    console.log('User authenticated, redirecting to:', from)
    
    // If it's an external URL (like the simulator), use window.location
    if (returnUrl && (returnUrl.startsWith('http://') || returnUrl.startsWith('https://'))) {
      window.location.href = returnUrl
      return null
    }
    
    return <Navigate to={from} replace />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="w-full border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <a href="https://www.pagemind.ai" className="flex items-center">
              <img src="/pagemind-logo.png" alt="PageMind" className="h-8 w-auto" />
            </a>
            <div className="hidden sm:flex sm:items-center sm:space-x-6">
              <a href="https://www.pagemind.ai/features" className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                Features
              </a>
              <a href="https://www.pagemind.ai/demo/aeo-impact" className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                Demo
              </a>
              <a href="https://www.pagemind.ai/pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                Pricing
              </a>
              <a href="/signup" className="text-sm font-medium text-primary hover:underline">
                Sign Up
              </a>
            </div>
          </div>
        </nav>
      </header>

      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-3xl font-bold">Welcome Back</h1>
            <p className="mt-2 text-muted-foreground">Sign in to manage your analytics, experiences, and growth.</p>
          </div>

          <div className="mt-8 space-y-4">
          {initError && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
              {initError}
            </div>
          )}
          
          {error && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="flex flex-col items-center space-y-4">
            {/* Show dev login prominently if Google Sign-In fails or in dev mode */}
            {(import.meta.env.DEV || initError) && (
              <div className="w-full">
                <DevLogin />
                {!initError && (
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Only show Google Sign-In if no error */}
            {!initError && (
              <div id="google-signin" className="w-full flex justify-center"></div>
            )}
            
            {/* Show legacy sign-in if modern one fails */}
            {initError && !import.meta.env.DEV && (
              <div className="w-full">
                <p className="text-center text-sm text-muted-foreground mb-2">
                  Try alternative sign-in method:
                </p>
                <GoogleSignInLegacy />
              </div>
            )}
            
            {loading && (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Authenticating...</span>
              </div>
            )}
          </div>

          <div className="text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <a href="/signup" className="font-medium text-primary hover:underline">
              Sign up
            </a>
          </div>
        </div>
        </div>
      </div>
    </div>
  )
}